import { Artwork } from '../artwork/Artwork'

export type GAEvent =
  'add_payment_info' |
  'add_shipping_info' |
  'begin_checkout' |
  'view_cart' |
  'add_to_cart' |
  'remove_from_cart' |
  'view_item' |
  'view_item_list' |
  'order_custom_painting'

interface ShippingDetails {
  billing: {
    address?: string,
    business_id?: string,
    business_name?: string,
    city?: string,
    country_id?: string,
    email?: string,
    first_name?: string,
    last_name?: string,
    phone?: string,
    zip?: string,
  },
  delivery_method?: string
}

export const useGTag = () => {
  const gtag = window.gtag;

  const fireEvent = (event: GAEvent, params: Record<string, unknown>) => {
    if (gtag) {
      gtag('event', event, params)
    }
  };

  const emit = (event: GAEvent, params: Record<string, unknown>) => {
    fireEvent(event, params)
  };

  const makeItem = ({
                      artwork,
                      discountCoupon,
                      index
                    }: { artwork: Record<string, any>, discountCoupon?: Record<string, any>, index: number }) => {
    const discountRateValue = (discountCoupon || {}).discount_rate_value

    const price = (artwork.price_value / 100)
    const discount = discountRateValue ? (discountRateValue / 100) : undefined
    const discountPrice = discount ? (price * discount) : undefined
    const categories = [
      (artwork.category || {}).name,
      (artwork.technique || {}).name,
      artwork.orientation,
      ...(artwork.themes || []).map(theme => theme.name),
    ].filter(Boolean)

    return {
      item_id: artwork.id,
      item_name: artwork.title,
      affiliation: 'Taiko',
      coupon: (discountCoupon || {}).code,
      discount: discountPrice,
      index,
      item_brand: (artwork.artist || {}).name,
      item_category: categories[0],
      item_category2: categories[1],
      item_category3: categories[2],
      item_category4: categories[3],
      item_category5: categories[4],
      item_list_id: 'cart_list',
      item_list_name: 'Cart list',
      price,
      quantity: 1
    }

  }

  const addPaymentInfoFromCart = (cart: any) => {
    const discountCoupon = cart.discount_coupon || {}
    emit('add_payment_info', {
      currency: 'EUR',
      value: cart.total.amount_value / 100,
      coupon: discountCoupon.code,
      items: cart.artworks.map((artwork, index) => {
        return makeItem({ artwork, discountCoupon, index })
      })
    })
  }

  const addShippingInfo = (cart: any, shippingDetails: ShippingDetails) => {
    const discountCoupon = cart.discount_coupon || {}

    emit('add_shipping_info', {
      currency: 'EUR',
      value: cart.total.amount_value / 100,
      coupon: discountCoupon.code,
      shipping_tier: shippingDetails.delivery_method,
      items: cart.artworks.map((artwork, index) => {
        return makeItem({ artwork, discountCoupon, index })
      })
    })
  }

  const beginCheckout = (cart: any) => {
    const discountCoupon = cart.discount_coupon || {}

    emit('begin_checkout', {
      currency: 'EUR',
      value: cart.total.amount_value / 100,
      coupon: cart.coupon,
      items: cart.artworks.map((artwork, index) => {
        return makeItem({ artwork, discountCoupon, index })
      })
    })
  }

  const viewCart = (artworks: Artwork[]) => {
    emit('view_cart', {
      currency: 'EUR',
      value: artworks.reduce((prev, next)=>{
        return prev + next.price_value
      }, 0),
      items: artworks.map((artwork, index) => {
        return makeItem({ artwork, index })
      })
    })
  }

  const addToCart = (artwork: Artwork) => {
    emit('add_to_cart', {
      currency: 'EUR',
      value: artwork.price_value,
      items: [makeItem({ artwork, index: 0 })]
    })
  }
  const removeFromCart = (artwork: Artwork) => {
    emit('remove_from_cart', {
      currency: 'EUR',
      value: artwork.price_value,
      items: [makeItem({ artwork, index: 0 })]
    })
  }

  const viewItem = (artwork: Artwork) => {
    emit('view_item', {
      currency: 'EUR',
      value: artwork.price_value,
      items: [makeItem({ artwork, index: 0 })]
    })
  }

  const viewItemList = ({ id, name, artworks }: { id: string, name: string, artworks: Artwork[] }) => {
    console.table(artworks)
    emit('view_item_list', {
      item_list_id: id,
      item_list_name: name,
      items: artworks.map((artwork, index) => {
        return makeItem({ artwork, index })
      })
    })
  }

  const generateUID = ()=>{
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 0x0f) >> (c === 'x' ? 0 : 1);
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
  const submitCommission = ({email}: {email: string})=>{
    emit('order_custom_painting', {
      custom_order_id: generateUID(),
      enhanced_conversion_data: {
        email
      }
    })
  }

  return {
    emit,
    addPaymentInfoFromCart,
    addShippingInfo,
    beginCheckout,
    viewCart,
    addToCart,
    removeFromCart,
    viewItem,
    viewItemList,
    submitCommission
  }
}
